import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { mergeMap } from 'rxjs/operators';

import { DocumentService } from '../../../core/api/document.service';
import { StatusEnum } from '../../../shared/constants/site_status.enum';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Document, Site } from '../../../shared/models';
import * as fromApp from '../../../store';
import { sendContract } from '../../../store/site/site.actions';

@Component({
  selector: 'fc-site-approval-step-3',
  templateUrl: './site-approval-step-3.component.html',
  styleUrls: ['./site-approval-step-3.component.scss'],
})
export class SiteApprovalStep3Component {
  @ViewChild('documentUpload', { static: false }) documentUpload: ElementRef;

  @Input() site: Site;
  @Input() isAdmin = false;
  @Input() expanded = false;
  @Input() currentUser = null;

  @Output() doChangeSiteState: EventEmitter<StatusEnum> = new EventEmitter<StatusEnum>();

  fileUploadMessage = '';
  uploadInProgress = false;

  constructor(
    public dialog: MatDialog,
    readonly documentService: DocumentService,
    readonly store: Store<fromApp.AppState> // private router: Router
  ) {}

  onActivated() {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Please Confirm',
          line1: 'This will update the site status. Are you sure you want to continue?',
          line2: '',
          buttonTextYes: 'Yes, Activate',
          buttonTextNo: 'No, Cancel',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.doChangeSiteState.emit(StatusEnum.ACTIVE);
        }
      });
  }

  uploadDocument(file: File): void {
    this.fileUploadMessage = 'Uploading...';
    this.uploadInProgress = true;
    this.documentService
      .uploadFile(this.site.identifier, file)
      .pipe(
        mergeMap(fileKey => {
          const newDoc = new Document({
            siteId: this.site.id,
            documentTypeId: this.documentService.getDocTypeId('Contract'),
            name: file.name,
            identifier: fileKey,
            userId: this.currentUser.id,
          });

          return this.documentService.createDocument(newDoc);
        })
      )
      .subscribe(
        document => {
          this.fileUploadMessage = document.name + ' uploaded successfully.';
          this.uploadInProgress = false;
        },
        () => {
          this.fileUploadMessage = 'ERROR: uploading file';
          this.uploadInProgress = false;
        }
      );
  }

  selectDocument(): void {
    const documentUpload = this.documentUpload.nativeElement;
    documentUpload.onchange = () => {
      if (documentUpload.files.length === 1) {
        const file = documentUpload.files[0];
        this.uploadDocument(file);
        this.documentUpload.nativeElement.value = '';
      }
    };
    documentUpload.click();
  }

  resendContract(): void {
    this.store.dispatch(sendContract({ identifier: this.site.identifier }));
  }

  get isCurrentStep() {
    return (
      this.site.approvedDate !== null &&
      this.site.activationDate === null &&
      this.site.status !== StatusEnum.APPROVED &&
      this.site.status !== StatusEnum.APPROVED_CONDITIONALLY
    );
  }

  get stepComplete() {
    return this.site?.activationDate !== null;
  }

  protected readonly StatusEnum = StatusEnum;
}
